import {
  faEnvelope,
  faLightbulbOn,
  faLock,
  faLockAlt,
} from '@fortawesome/pro-regular-svg-icons'
import { Formik, FormikHelpers as FormikActions } from 'formik'
import React, { useEffect } from 'react'
import { connect } from 'react-redux'
import { RouteComponentProps } from 'react-router'
import { withRouter } from 'react-router-dom'
import * as Yup from 'yup'
import { ValidationError } from 'epc-schema'
import { login, updateSession } from 'session'
import { Content, Form, Indicators, Input } from 'ui'
import { RequiredEmail, RequiredString } from 'validations'
import { useCreateUser, useCurrentProfile } from '../graphql'

/* BEGIN - Form Values */
/**
 * This interface represents potential form values and
 * is passed into our custom form field factories to get
 * type safety on our form.
 */
interface IConfirmAccountValues {
  [key: string]: string | undefined
  emailAddress?: string | undefined
  password?: string | undefined
  passwordConfirm?: string | undefined
  passwordHint?: string | undefined
  profileId?: string | undefined
}

/**
 * This schema is used by formik to provide client side
 * form validation.
 */
const ConfirmAccountSchema = Yup.object().shape({
  emailAddress: RequiredEmail(),
  password: RequiredString().min(8),
  passwordConfirm: RequiredString()
    .oneOf([Yup.ref('password')], 'must match password')
    .required('Password confirm is required'),
  passwordHint: RequiredString(),
})

/**
 * These are the actual default values supplied to the form
 * if a profile is not loaded via the network. Any missing
 * values from the network fetched profile will have these
 * values substituted as well.
 */
const blankValues: IConfirmAccountValues = {
  emailAddress: '',
  password: '',
  passwordConfirm: '',
  passwordHint: '',
  profileId: '',
}
/* END - Form Values */

/* BEGIN - Custom Form Renderers */
const FormField = Input.Renderer<IConfirmAccountValues>()
const ErrorSummary = Form.ErrorSummaryRenderer<IConfirmAccountValues>()
/* END - Custom Form Renderers */

interface IParams {
  role?: string
}

interface IFormProps extends RouteComponentProps<IParams> {
  dispatch: (action: any) => any
}

/**
 * Creates a new user account assuming the user has
 * a temporary access token.
 */
const FormView: React.FC<IFormProps> = (props) => {
  const {
    educator,
    error: queryError,
    getCurrentProfile,
    loading,
    professional,
  } = useCurrentProfile()

  useEffect(() => {
    getCurrentProfile()
  }, [getCurrentProfile])

  const profile = educator || professional || null
  const effectiveRole = profile ? profile.role : props.match.params.role

  const [createUser, { error: mutationError }] = useCreateUser()

  const handleSubmitForm = async (
    values: IConfirmAccountValues,
    actions: FormikActions<any>
  ) => {
    const result = await createUser({
      variables: {
        userInput: {
          emailAddress: values.emailAddress as string,
          password: values.password as string,
          passwordConfirm: values.passwordConfirm as string,
          passwordHint: values.passwordHint as string,
          profileId: values.profileId as string,
        },
      },
    })
    if (
      result.data &&
      !result.data.response.success &&
      result.data.response.errors
    ) {
      result.data.response.errors
        .map((e) => e as ValidationError)
        .forEach((fieldError) =>
          actions.setFieldError(`${fieldError.path}`, fieldError.message)
        )
    } else {
      const auth = {
        email: values.emailAddress as string,
        password: values.password as string,
      }
      try {
        const authTokens = await login(
          process.env.REACT_APP_EPC_API_URL ??
            process.env.REACT_APP_API_URL ??
            '',
          auth
        )
        props.dispatch(
          updateSession({ ...authTokens, temporaryAccessToken: '' })
        )
      } catch (e) {
        actions.setFieldError('_', e.message)
        return
      }
      props.history.replace(
        `/signup/${(effectiveRole || '').toLowerCase()}/one`
      )
      return
    }
    actions.setSubmitting(false)
  }

  if (loading) {
    return <Indicators.Spinner />
  }

  const defaultValues = (profile as any) || blankValues
  const initialValues = Object.keys(defaultValues).reduce(
    (o, k) => ({ ...o, [k]: defaultValues[k] || o[k] }),
    blankValues
  )

  if (queryError) {
    return (
      <Form.Form>
        <Form.Row>
          <Form.Item width={[1]}>
            <Form.ErrorComponent
              error={`There was a problem connecting to the Pro Connect server. Please try refreshing this page.`}
            />
          </Form.Item>
        </Form.Row>
      </Form.Form>
    )
  }

  return (
    <Formik
      initialValues={initialValues}
      onSubmit={handleSubmitForm}
      validationSchema={ConfirmAccountSchema}
    >
      {({ handleSubmit, isSubmitting, errors, submitCount, submitForm }) => {
        return (
          <Form.Form onSubmit={handleSubmit}>
            <Form.Row>
              <Form.Item width={[1]}>
                <FormField
                  disabled={true}
                  icon={faEnvelope}
                  name="emailAddress"
                  label="Email"
                  type="text"
                  required={true}
                />
              </Form.Item>
            </Form.Row>
            <Form.Row>
              <Form.Item width={[1]}>
                <FormField
                  disabled={isSubmitting}
                  icon={faLock}
                  name="password"
                  label="Password"
                  type="password"
                  required={true}
                />
              </Form.Item>
            </Form.Row>
            <Form.Row>
              <Form.Item width={[1]}>
                <FormField
                  disabled={isSubmitting}
                  icon={faLockAlt}
                  name="passwordConfirm"
                  label="Confirm Password"
                  type="password"
                  required={true}
                />
              </Form.Item>
            </Form.Row>
            <Form.Row>
              <Form.Item width={[1]}>
                <FormField
                  disabled={isSubmitting}
                  icon={faLightbulbOn}
                  name="passwordHint"
                  label="Hint"
                  placeholder="In case you forget"
                  type="text"
                  required={true}
                />
              </Form.Item>
            </Form.Row>
            <Form.ErrorComponent
              error={mutationError ? mutationError.message : null}
            />
            <Form.GeneralError name="_" />
            {submitCount > 0 ? <ErrorSummary errors={errors} /> : null}
            <Form.Row>
              <Form.Item width={1}>
                <Content.ActionButton
                  type="submit"
                  loading={isSubmitting}
                  onClick={submitForm}
                  label="Confirm my Account & Build Profile"
                />
              </Form.Item>
            </Form.Row>
          </Form.Form>
        )
      }}
    </Formik>
  )
}

export const ConfirmAccount = connect()(withRouter(FormView))
